/**
 * Cart Slide - Vanilla JavaScript Version
 * Replaces Rivets.js and CartJS with native JavaScript
 * Uses VanillaCartState for reactive state management
 */

(function () {
  if (customElements.get('cart-slide')) return;

  // Enhanced debounce utility function with cancel support
  function debounce(func, wait) {
    let timeout;
    const debounced = function executedFunction(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
    debounced.cancel = () => clearTimeout(timeout);
    return debounced;
  }

  function isAddToCartButton(el) {
    if (!el || !el.classList) return false;
    return el.classList.contains('product_atc_button') || el.classList.contains('add-to-cart') || el.hasAttribute('data-add-to-cart');
  }

  class CartSlide extends HTMLElement {
    constructor() {
      super();
      this.timerInterval = null;
      this.reserveEndTime = null;
      this.reserveTimeRemaining = null;
      this._discountUpdateInProgress = false;
      this._quantityUpdateInProgress = new Set();
      this._overlayClickHandler = null;
      this._cachedElements = new Map();
      this._abortControllers = new Map();
      this._productDataCache = {};

      // Initialize cart state
      this.cartState = window.VanillaCartState;

      // Accessibility / focus management
      this._openerEl = null;
      this._onKeydown = (e) => {
        if (!this.classList.contains('active')) return;
        if ((e.code || '').toUpperCase() !== 'ESCAPE') return;
        e.preventDefault();
        e.stopPropagation();
        this.closeCart();
      };

      // Drawer-owned focus trap (does not rely on global trapFocus state)
      this._onTrapTabKeydown = (e) => {
        if (!this.classList.contains('active')) return;
        const code = (e.code || '').toUpperCase();
        const key = (e.key || '').toUpperCase();
        if (code !== 'TAB' && key !== 'TAB') return;

        const focusables = Array.from(
          this.querySelectorAll(
            'a[href], button:enabled, input:not([type="hidden"]):enabled, select:enabled, textarea:enabled, [tabindex]:not([tabindex^="-"])'
          )
        ).filter((el) => {
          if (!el || typeof el.focus !== 'function') return false;
          if (el.disabled) return false;
          const style = window.getComputedStyle ? window.getComputedStyle(el) : null;
          if (style && (style.display === 'none' || style.visibility === 'hidden')) return false;
          if (el.offsetParent === null && el.getClientRects().length === 0) return false;
          return true;
        });

        if (!focusables.length) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        // If focus escaped, pull it back in and stop native tabbing
        if (!this.contains(document.activeElement)) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
          return;
        }

        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
          return;
        }

        if (e.shiftKey && (document.activeElement === first)) {
          e.preventDefault();
          last.focus();
          return;
        }
      };

      this.setupPolicyAgreement();
    }

    connectedCallback() {
      this.setupListeners();
      this.setupCartStateSubscriptions();
      this.refreshCart();
      this.restoreDiscountUIFromStorage();
      this.toggleCart = this.toggleCart.bind(this);
      this.openCart = this.openCart.bind(this);
      this.closeCart = this.closeCart.bind(this);

      const inEditor = this._inDesignMode();
      const shouldRestoreDrawer = inEditor && window.__cartDrawerOpen === true;

      // Keep cart drawer open in theme editor after section re-render.
      if (shouldRestoreDrawer) {
        this.openCart({ suppressFocus: true, instant: true });
      } else if (!this.classList.contains('active')) {
        // Ensure closed drawer isn't keyboard-focusable.
        this._setA11yState(false);
      }
    }

    /**
     * Cleanup when element is removed from DOM
     */
    disconnectedCallback() {
      // Clear timer
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }

      // Cancel debounced updates
      if (this.debouncedQuantityUpdate && this.debouncedQuantityUpdate.cancel) {
        this.debouncedQuantityUpdate.cancel();
      }

      // Remove overlay click handler
      const overlay = document.getElementById('cart-overlay');
      if (overlay && this._overlayClickHandler) {
        overlay.removeEventListener('click', this._overlayClickHandler);
      }

      document.removeEventListener('keydown', this._onKeydown, true);
      document.removeEventListener('keydown', this._onTrapTabKeydown, true);

      // Abort any pending requests
      this._abortControllers.forEach(controller => controller.abort());
      this._abortControllers.clear();

      // Clear caches
      this._cachedElements.clear();
      this._cartBubbleCache = null;
      this._productDataCache = {};
    }

    /**
     * Set up subscriptions to cart state changes
     */
    setupCartStateSubscriptions() {
      // Subscribe to cart updates
      document.addEventListener('cart:updated', (e) => {
        const cart = e.detail;
        this.onCartUpdated(cart);
      });
    }

    /**
     * Handle cart updates
     */
    onCartUpdated(cart) {
      this.updateCartDisplay(cart);
      this.updateGoals(cart);
      this.toggleCheckout();
      this.updateCartIconCount(cart.item_count);
      this.syncQuantityInputs(cart);
      this.enrichCart(cart);
      this.restoreCartImages();
    }

    /**
     * Update cart display elements
     */
    updateCartDisplay(cart) {
      // Update item count
      const itemCountElements = this.querySelectorAll('[data-bind-text="cart.item_count"]');
      itemCountElements.forEach(el => {
        el.textContent = cart.item_count || 0;
      });

      // Update item/items text
      const itemSingular = this.querySelectorAll('[data-bind-show="cart.item_count|eq:1"]');
      itemSingular.forEach(el => {
        el.style.display = cart.item_count === 1 ? 'inline' : 'none';
      });

      const itemPlural = this.querySelectorAll('[data-bind-hide="cart.item_count|eq:1"]');
      itemPlural.forEach(el => {
        el.style.display = cart.item_count !== 1 ? 'inline' : 'none';
      });

      // Render cart items
      this.renderCartItems(cart.items || []);

      // Update summary
      this.updateSummary(cart);
    }

    /**
     * Render cart items using template
     */
    renderCartItems(items) {
      const container = this.querySelector('[data-cart-items-container]');
      if (!container) return;

      const template = document.getElementById('cart-item-template');
      if (!template) {
        // Fallback: just update existing items
        return;
      }

      // Find existing empty cart message in container (it's rendered by Liquid template)
      let emptyCartMsg = container.querySelector('.cart-slide_empty_cart');
      let emptyMsgText = 'Your cart is empty';
      
      // Preserve the empty cart message text if it exists
      if (emptyCartMsg) {
        emptyMsgText = emptyCartMsg.textContent.trim();
      } else {
        // Try to get text from elsewhere in the document
        const existingEmptyMsg = document.querySelector('.cart-slide_empty_cart');
        if (existingEmptyMsg) {
          emptyMsgText = existingEmptyMsg.textContent.trim();
        }
      }

      const visibleItems = (items || []).filter((item) => this.shouldShowCartItem(item));

      // Show empty cart message if no items
      if (!visibleItems.length) {
        container.innerHTML = `<li class="cart-slide_empty_cart" style="display: block; text-align: center; padding: 40px 20px; list-style: none; width: 100%;">${emptyMsgText}</li>`;
        return;
      }

      // Clear container and render items (empty cart message will be hidden)
      container.innerHTML = '';

      // Render each item (pass the <li> so querySelector works inside it)
      visibleItems.forEach(item => {
        const clone = template.content.cloneNode(true);
        const li = clone.firstElementChild;
        if (li) {
          this.populateCartItem(li, item);
          container.appendChild(clone);
        }
      });
    }

    isShippingBarGiftItem(item) {
      const props = item?.properties || {};
      return props._shipping_bar_gift === '1' || props._shipping_bar_gift === 1;
    }

    isGiftBlockCartItem(item) {
      const props = item?.properties || {};
      return props._gift_block_cart === '1' || props._gift_block_cart === 1;
    }

    isAutoGiftItem(item) {
      return this.isShippingBarGiftItem(item) || this.isGiftBlockCartItem(item);
    }

    shouldShowCartItem(item) {
      if (!this.isShippingBarGiftItem(item)) return true;

      const bar = this.querySelector('shipping-bar');
      const gifts = bar && typeof bar.getGiftMilestones === 'function'
        ? bar.getGiftMilestones()
        : [];
      const match = gifts.find((gift) => String(gift.giftVariantId) === String(item.variant_id || item.id));
      if (!match) return false;

      const cart = this.cartState?.cart;
      const spendCents = typeof window.getProgressBarCartTotal === 'function'
        ? window.getProgressBarCartTotal(cart)
        : (Number(cart?.total_price) || 0);
      return (spendCents / 100) >= match.price;
    }

    /**
     * Populate cart item template with data
     */
    populateCartItem(element, item) {
      // Product image
      const img = element.querySelector('[data-bind-src]');
      if (img) {
        if (item.image) {
          img.src = this.getImageUrl(item.image, '100x100');
          img.alt = item.product_title || '';
          img.style.display = '';
        } else {
          img.style.display = 'none';
        }
      }

      const noImagePlaceholder = element.querySelector('[data-bind-hide="item.image"]');
      if (noImagePlaceholder) {
        noImagePlaceholder.style.display = item.image ? 'none' : 'flex';
      }

      // Product title
      const titleEl = element.querySelector('[data-bind-text="item.product_title"]');
      if (titleEl) titleEl.textContent = item.product_title || '';

      // Vendor
      const vendorEl = element.querySelector('[data-bind-text="item.vendor"]');
      if (vendorEl) vendorEl.textContent = item.vendor || '';

      // Variant title
      const variantEl = element.querySelector('[data-bind-text="item.variant_title"]');
      if (variantEl) {
        const variantDetailsHtml = this.formatVariantDetails(item);
        variantEl.innerHTML = variantDetailsHtml;
        const showVariant = Boolean(variantDetailsHtml);
        variantEl.style.display = showVariant ? '' : 'none';
      }

      // Quantity controls
      const variantId = item.variant_id;
      const minusBtn = element.querySelector('.cart-qty-minus');
      const plusBtn = element.querySelector('.cart-qty-plus');
      const qtyInput = element.querySelector('.cart-qty-input');
      const errorSpan = element.querySelector('[data-render-error]');

      if (minusBtn) minusBtn.dataset.variantId = variantId;
      if (plusBtn) plusBtn.dataset.variantId = variantId;
      if (qtyInput) {
        qtyInput.dataset.variantId = variantId;
        qtyInput.value = item.quantity || 0;
      }
      if (errorSpan) errorSpan.dataset.variantId = variantId;

      // Prices: compare-at / original and current line price
      const compareAtPrice = item.compare_at_price ?? item.variant?.compare_at_price ?? 0;
      const compareAtLine = (item.compare_at_line_price != null)
        ? Number(item.compare_at_line_price)
        : (compareAtPrice > item.price ? compareAtPrice * (item.quantity || 1) : 0);
      const linePrice = Number(item.line_price) || 0;
      const originalLine = Number(item.original_line_price) || 0;
      const showCompare = compareAtLine > linePrice;
      const showOriginal = originalLine > linePrice && !showCompare;

      const oldPriceOriginal = element.querySelector('.old-price--original, [data-item-old-original]');
      if (oldPriceOriginal) {
        oldPriceOriginal.style.display = showOriginal ? '' : 'none';
        if (showOriginal) oldPriceOriginal.innerHTML = this.cartState.formatMoney(originalLine);
      }

      const oldPriceCompare = element.querySelector('.old-price--compare, [data-item-old-compare]');
      if (oldPriceCompare) {
        oldPriceCompare.style.display = showCompare ? '' : 'none';
        if (showCompare) oldPriceCompare.innerHTML = this.cartState.formatMoney(compareAtLine);
      }

      const newPrice = element.querySelector('[data-item-line-price]') || element.querySelector('.new-price');
      if (newPrice) newPrice.innerHTML = this.cartState.formatMoney(linePrice);

      const qtyWrapper = element.querySelector('.cart-item-qty-wrapper');
      if (this.isAutoGiftItem(item)) {
        element.classList.add('cart-item--free-gift');
        if (newPrice) newPrice.textContent = 'Free';
        if (oldPriceOriginal) oldPriceOriginal.style.display = 'none';
        if (oldPriceCompare) oldPriceCompare.style.display = 'none';
        if (qtyWrapper) qtyWrapper.style.display = 'none';
        if (minusBtn) minusBtn.disabled = true;
        if (plusBtn) plusBtn.disabled = true;
        if (qtyInput) qtyInput.disabled = true;
      }
    }

    /**
     * Get image URL with size
     */
    getImageUrl(url, size) {
      if (!url || !size) return url || '';
      // Hippie Farms edit #33 (2026-09-24): same-domain CDN path and a width parameter, not the legacy
      // cdn.shopify.com _WxH suffix (did not show on Ryan's phone).
      const own = url.replace(/^(https?:)?\/\/cdn\.shopify\.com\/s\/files\/\d+\/\d+\/\d+\/\d+\//, '/cdn/shop/');
      const width = parseInt(size, 10) * 2 || 200;
      return own + (own.includes('?') ? '&' : '?') + 'width=' + width;
    }

    escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    formatVariantDetails(item) {
      const options = Array.isArray(item?.options_with_values) ? item.options_with_values : [];
      const visibleOptions = options.filter((option) => {
        const optionName = String(option?.name || '').trim().toLowerCase();
        const optionValue = String(option?.value || '').trim().toLowerCase();
        if (!optionName || !optionValue) return false;
        return !(optionName === 'title' && optionValue === 'default title');
      });

      if (visibleOptions.length > 0) {
        const optionsHtml = visibleOptions
          .map(
            option =>
              `<div><strong class="cart-item-variant-label">${this.escapeHtml(option.name)}:</strong> ${this.escapeHtml(option.value)}</div>`
          )
          .join('');
        return optionsHtml;
      }

      const variantTitle = item?.variant_title;
      if (variantTitle && variantTitle !== 'Default Title') {
        return this.escapeHtml(variantTitle);
      }

      return '';
    }

    /**
     * Resolve applied promo/discount code title for summary labeling
     */
    getPromoCodeTitle(cart) {
      const apps = cart?.cart_level_discount_applications || [];
      if (apps.length) {
        const app = apps[0];
        const fromApp = app.title || app.code || '';
        if (fromApp) return fromApp;
      }

      // Line-item discount titles (some codes allocate here instead of cart-level)
      if (cart?.items?.length) {
        for (const item of cart.items) {
          const discounts = item.discounts || [];
          for (const discount of discounts) {
            const title = discount.title || discount.code || '';
            if (title) return title;
          }
        }
      }

      // Applied UI chip in the discount block
      const codeSpan = this.querySelector('.cart-slide__discount-code');
      const fromUi = codeSpan?.textContent?.trim();
      if (fromUi) return fromUi;

      try {
        return window.sessionStorage ? (sessionStorage.getItem('cartDrawerDiscountCode') || '') : '';
      } catch (e) {
        return '';
      }
    }

    /**
     * Format a savings amount with a leading minus for cart summary clarity
     */
    formatSavingsMoney(cents) {
      const amount = Number(cents) || 0;
      if (amount <= 0) return '';
      return `-${this.cartState.formatMoney(amount)}`;
    }

    /**
     * Update cart summary section
     */
    updateSummary(cart) {
      const summary = this.querySelector('.cart-slide__summary');
      const promoDiscount = Number(cart.total_discount) || 0;

      // Promo / discount code savings
      const promoRow = this.querySelector('[data-savings-promo-row]')
        || this.querySelector('.cart-slide__savings--promo')
        || this.querySelector('[data-bind-show="cart.total_discount|gt:0"]');
      if (promoRow) {
        promoRow.style.display = promoDiscount > 0 ? 'flex' : 'none';
        const promoAmount = promoRow.querySelector('[data-savings]')
          || promoRow.querySelector('[data-bind-html="cart.total_discount|money"]');
        if (promoAmount) {
          promoAmount.innerHTML = this.formatSavingsMoney(promoDiscount);
        }

        const promoLabel = promoRow.querySelector('[data-savings-promo-label]');
        if (promoLabel && summary) {
          const code = this.getPromoCodeTitle(cart);
          const baseLabel = summary.dataset.promoLabel || 'Promo code';
          const withCodeTemplate = summary.dataset.promoLabelWithCode || 'Promo code (__CODE__)';
          promoLabel.textContent = code
            ? `${withCodeTemplate.replace('__CODE__', code)}:`
            : `${baseLabel}:`;
        }
      }

      // Sale / compare-at savings
      const savingsRow = document.getElementById('cart-drawer-savings-compare')
        || this.querySelector('.cart-slide__savings--compare')
        || this.querySelector('[data-savings-compare-row]');
      let saleSavings = cart.total_compare_at_savings != null ? Number(cart.total_compare_at_savings) : 0;
      if (saleSavings === 0 && cart.items && cart.items.length) {
        saleSavings = cart.items.reduce((sum, item) => {
          const cp = item.compare_at_price != null ? Number(item.compare_at_price) : 0;
          const p = item.price != null ? Number(item.price) : 0;
          const qty = item.quantity || 0;
          if (cp > p) return sum + (cp - p) * qty;
          return sum;
        }, 0);
      }
      if (savingsRow) {
        savingsRow.style.display = saleSavings > 0 ? 'flex' : 'none';
        const savingsAmount = savingsRow.querySelector('[data-savings-compare-amount]');
        if (savingsAmount) {
          savingsAmount.textContent = this.formatSavingsMoney(saleSavings);
        }
      }

      // Combined total savings (only when both promo + sale are present)
      const totalSavings = promoDiscount + saleSavings;
      const totalRow = this.querySelector('[data-savings-total-row]');
      if (totalRow) {
        const showTotal = promoDiscount > 0 && saleSavings > 0;
        totalRow.style.display = showTotal ? 'flex' : 'none';
        const totalAmount = totalRow.querySelector('[data-savings-total-amount]');
        if (totalAmount) {
          totalAmount.textContent = showTotal ? this.formatSavingsMoney(totalSavings) : '';
        }
      }

      // Update subtotal (summary block) — keep cart.total_price, do not subtract savings again
      const subtotalAmount = this.querySelector('.cart-slide__summary [data-subtotal-amount]')
        || this.querySelector('[data-subtotal-amount]')
        || this.querySelector('.cart-slide__subtotal strong');
      if (subtotalAmount && cart.total_price != null) {
        subtotalAmount.innerHTML = this.cartState.formatMoney(cart.total_price);
      }

      // Hippie Farms sandbox edit #27 (2026-09-23): the checkout button carries the order total ("Checkout - $44.99").
      const checkoutText = this.querySelector('.cart-slide__checkout-btn-text[data-hf-checkout-base]');
      if (checkoutText && cart.total_price != null) {
        const base = checkoutText.dataset.hfCheckoutBase;
        const next = cart.item_count > 0 ? `${base} - ${this.cartState.formatMoney(cart.total_price)}` : base;
        if (checkoutText.textContent !== next) checkoutText.textContent = next;
      }
    }

    setupListeners() {
      const closeBtn = this.querySelector('.cart-slide__close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          this.closeCart();
        });
      }

      this._ensureOverlay();

      // Debounced quantity update function with proper cleanup
      this.debouncedQuantityUpdate = debounce(async (variantId, newQty, inputElement) => {
        // Ensure clean state before update
        this._quantityUpdateInProgress.add(variantId);

        try {
          const cart = await this.cartState.updateItemById(variantId, newQty);
          this.hideErrorForVariant(variantId);

          // Update input value to match actual quantity
          if (cart && cart.items && inputElement) {
            const updatedItem = cart.items.find(item => item.variant_id === variantId);
            if (updatedItem) {
              inputElement.value = updatedItem.quantity;
            } else if (newQty === 0) {
              // Item was removed
              inputElement.value = 0;
            }
          }

          this.updateCartIconCount(cart.item_count);
        } catch (error) {
          this.handleQuantityUpdateError(variantId, error, inputElement);
        } finally {
          // Always clean up, even if error occurs
          this._quantityUpdateInProgress.delete(variantId);
          this.reEnableButtonsForVariant(variantId);
        }
      }, 300);

      // Close on Escape when open
      document.addEventListener('keydown', this._onKeydown, true);
      // Trap Tab navigation inside drawer (capture phase)
      document.addEventListener('keydown', this._onTrapTabKeydown, true);

      // Handle addon toggles: checked = add product, unchecked = remove
      this.addEventListener('change', (e) => {
        const target = e.target;
        if (target.classList.contains('product-addons__toggle')) {
          const itemEl = target.closest('.product-addons__item[data-variant-id]');
          const variantId = itemEl?.dataset?.variantId;
          if (!variantId) return;
          e.preventDefault();
          this.handleAddonToggle(target, variantId);
          return;
        }
      });

      // Handle quantity input changes
      this.addEventListener('change', (e) => {
        const target = e.target;

        // Handle quantity input field changes
        if (target.classList.contains('cart-qty-input')) {
          const variantId = target.dataset.variantId; // Keep as string to preserve format like "id:hash"
          let newQty = parseInt(target.value, 10);

          if (!variantId || isNaN(newQty)) {
            return;
          }

          // Check if update is already in progress
          if (this._quantityUpdateInProgress.has(variantId)) {
            return;
          }

          // Ensure quantity doesn't exceed max (if set)
          if (target.max && newQty > parseInt(target.max, 10)) {
            newQty = parseInt(target.max, 10);
            target.value = newQty;
          }

          // Mark as in progress and debounce the update
          this._quantityUpdateInProgress.add(variantId);
          this.debouncedQuantityUpdate(variantId, newQty, target);
        }
      });

      // Handle quantity button clicks (plus/minus)
      this.addEventListener('click', (e) => {
        const target = e.target.closest('.cart-qty-btn');

        if (target) {
          e.preventDefault();
          e.stopPropagation();

          const isPlus = target.classList.contains('cart-qty-plus');
          const isMinus = target.classList.contains('cart-qty-minus');

          const variantId = target.dataset.variantId; // Keep as string to preserve format like "id:hash"
          if (!variantId) return;

          // Check if update is already in progress
          if (this._quantityUpdateInProgress.has(variantId)) {
            return;
          }

          const input = target.parentElement.querySelector('.cart-qty-input');
          if (!input) return;

          // Disable buttons during update
          const buttons = target.parentElement.querySelectorAll('.cart-qty-btn');
          buttons.forEach(btn => {
            btn.disabled = true;
            btn.style.pointerEvents = 'none';
            btn.style.opacity = '0.6';
          });

          const currentQty = parseInt(input.value, 10) || 0;
          let newQty;

          if (isPlus) {
            newQty = currentQty <= 0 ? 1 : currentQty + 1;
            if (input.max && newQty > parseInt(input.max, 10)) {
              newQty = parseInt(input.max, 10);
            }
          } else if (isMinus) {
            newQty = Math.max(0, currentQty - 1);
          }

          input.value = newQty;
          this._quantityUpdateInProgress.add(variantId);
          this.debouncedQuantityUpdate(variantId, newQty, input);

          // Re-enable buttons after a short delay
          setTimeout(() => {
            if (!this._quantityUpdateInProgress.has(variantId)) {
              buttons.forEach(btn => {
                btn.disabled = false;
                btn.style.pointerEvents = '';
                btn.style.opacity = '';
              });
            }
          }, 350);
        }
      });

      // Discount button
      const discountBtn = this.querySelector('.cart-slide__btn');
      if (discountBtn) {
        discountBtn.addEventListener('click', () => {
          this.applyDiscount();
        });
      }

      // Discount removal
      this.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('.cart-slide__discount-remove');
        if (removeBtn) {
          e.preventDefault();
          e.stopPropagation();
          this.removeDiscount(removeBtn);
        }
      });

      // Checkout button
      const checkoutBtn = this.querySelector('.cart-slide__checkout-btn');
      if (checkoutBtn) {
        checkoutBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleCheckout();
        });
      }
    }

    async handleCheckout() {
      // Save note
      const noteWrapper = this.querySelector('.cart-slide__note');
      const noteTextarea = noteWrapper?.querySelector('.cart-slide__textarea');
      const note = noteTextarea?.value?.trim();

      if (noteWrapper && note) {
        try {
          await this.cartState.setNote(note);
        } catch (error) {
          console.error('Error saving note:', error);
        }
      }

      // Handle gift
      const giftBlock = document.querySelector('gift-block-cart');
      const giftContainer = giftBlock?.querySelector('.cart-slide__gift-item');
      const isGiftUnlocked = giftContainer && !giftContainer.classList.contains('cart-slide__gift-item--locked');
      const giftVariantId = giftBlock?.dataset.giftid;

      if (giftBlock && isGiftUnlocked && giftVariantId) {
        const cart = this.cartState.cart;
        const giftItem = cart.items?.find((item) => {
          const props = item.properties || {};
          const isGiftLine = props._gift_block_cart === '1' || props._gift_block_cart === 1;
          return isGiftLine && String(item.variant_id) === String(giftVariantId);
        });

        try {
          if (giftItem && giftItem.quantity > 1) {
            const line = (cart.items || []).findIndex((item) => item === giftItem) + 1;
            if (line > 0 && typeof this.cartState.updateItemByLine === 'function') {
              await this.cartState.updateItemByLine(line, 1);
            }
          } else if (!giftItem) {
            await this.cartState.addItem(giftVariantId, 1, { _gift_block_cart: '1' });
          }
        } catch (error) {
          console.error('Error handling gift:', error);
        }
      }

      // Handle auto-discount
      const discountBlock = this.querySelector('[data-unlockdiscount][data-unlockship]');
      const discountCode = discountBlock?.dataset.code?.toLowerCase();
      const isUnlocked = discountBlock?.classList.contains('unlocked');

      if (discountBlock && isUnlocked && discountCode) {
        this._discountUpdateInProgress = true;
        try {
          await this.applyDiscountViaIframe(discountCode);
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (error) {
          console.error('Error applying discount:', error);
        } finally {
          this._discountUpdateInProgress = false;
        }
      }

      // Redirect to checkout
      window.location.href = '/checkout';
    }

    toggleCheckout() {
      const checkbox = this.querySelector('.cart-slide__policy input[type="checkbox"]');
      const checkoutBtn = this.querySelector('.cart-slide__checkout-btn');

      if (!checkbox || !checkoutBtn) return;

      const cart = this.cartState.cart;
      const cartNotEmpty = cart.item_count > 0;
      const policyChecked = checkbox?.checked;

      checkoutBtn.disabled = !(cartNotEmpty && policyChecked);

      // Empty cart message is now handled in renderCartItems
    }

    _inDesignMode() {
      return typeof window.Shopify !== 'undefined' && !!window.Shopify.designMode;
    }

    _setEditorOpenState(isOpen) {
      if (this._inDesignMode()) {
        window.__cartDrawerOpen = !!isOpen;
      }
    }

    _ensureOverlay() {
      let overlay = document.getElementById('cart-overlay');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'cart-overlay';
        overlay.className = 'cart-overlay';
        document.body.appendChild(overlay);
      }
      if (!this._overlayClickHandler) {
        this._overlayClickHandler = () => this.closeCart();
      }
      if (!overlay.dataset.cartOverlayBound) {
        overlay.addEventListener('click', this._overlayClickHandler);
        overlay.dataset.cartOverlayBound = 'true';
      }
      return overlay;
    }

    _setA11yState(isOpen) {
      // inert blocks keyboard focus in supporting browsers; aria-hidden helps screen readers.
      if (isOpen) {
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('inert');
      } else {
        this.setAttribute('aria-hidden', 'true');
        this.setAttribute('inert', '');
      }
    }

    _focusFirstElement() {
      const closeBtn = this.querySelector('.cart-slide__close');
      if (closeBtn && typeof closeBtn.focus === 'function') {
        closeBtn.focus();
        return closeBtn;
      }
      const first = this.querySelector(
        'a[href], button:enabled, input:not([type="hidden"]):enabled, select:enabled, textarea:enabled, [tabindex]:not([tabindex^="-"])'
      );
      if (first && typeof first.focus === 'function') first.focus();
      return first || this;
    }

    openCart({ openerEl = null, suppressFocus = false, instant = false } = {}) {
      const overlay = this._ensureOverlay();

      if (openerEl) this._openerEl = openerEl;
      else if (!this._openerEl) this._openerEl = document.activeElement;

      if (this.classList.contains('active')) {
        this._setEditorOpenState(true);
        return;
      }

      if (instant) {
        this.style.transition = 'none';
        if (overlay) overlay.style.transition = 'none';
      }

      this.classList.add('active');
      overlay?.classList.add('active');
      document.body.classList.add('no-scroll');
      this._setA11yState(true);
      this._setEditorOpenState(true);

      this.clearStaleCartImages();
      const reservedEl = this.querySelector('.cart-slide__reserved');
      const minutes = parseInt(reservedEl?.getAttribute('data-time'), 10) || 5;
      this.startReserveTimer(minutes * 60);
      this.refreshCart();

      if (!suppressFocus) {
        const focusTarget = this._focusFirstElement();
        if (typeof trapFocus === 'function') trapFocus(this, focusTarget || this);
      }

      if (instant) {
        requestAnimationFrame(() => {
          this.style.transition = '';
          if (overlay) overlay.style.transition = '';
        });
      }
    }

    closeCart() {
      const overlay = document.getElementById('cart-overlay');

      if (!this.classList.contains('active')) {
        this._setEditorOpenState(false);
        return;
      }

      this.classList.remove('active');
      overlay?.classList.remove('active');
      document.body.classList.remove('no-scroll');

      this.pauseTimer();
      this.clearAllErrors();

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this._openerEl);
      } else if (this._openerEl && typeof this._openerEl.focus === 'function') {
        this._openerEl.focus();
      }
      this._setA11yState(false);
      this._setEditorOpenState(false);
    }

    toggleCart(openerEl = null) {
      if (this.classList.contains('active')) {
        this.closeCart();
      } else {
        this.openCart({ openerEl });
      }
    }

    clearStaleCartImages() {
      const cartItemImages = this.querySelectorAll('.cart-item-image img');
      cartItemImages.forEach(img => {
        img.style.opacity = '0';
        img.style.transition = 'opacity 0.3s';
      });
    }

    restoreCartImages() {
      const cartItemImages = this.querySelectorAll('.cart-item-image img');
      cartItemImages.forEach(img => {
        img.style.opacity = '1';
      });
    }

    clearAllErrors() {
      this.querySelectorAll('[data-render-error]').forEach(el => { el.textContent = ''; el.classList.add('hidden'); });
    }

    handleQuantityUpdateError(variantId, error, inputElement) {
      let errorMessage = '';

      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && error.message) {
        errorMessage = error.message;
      }

      if (errorMessage) {
        this.showErrorForVariant(variantId, errorMessage);
      }

      this._quantityUpdateInProgress.delete(variantId);

      // Reset to current quantity
      const cart = this.cartState.cart;
      const item = cart.items?.find(i => i.variant_id === variantId);
      if (inputElement && item) {
        inputElement.value = item.quantity;
      }

      this.reEnableButtonsForVariant(variantId);
    }

    _getVariantQtyContext(variantId) {
      const input = this.querySelector(`.cart-qty-input[data-variant-id="${variantId}"]`);
      return input ? { input, qtyWrapper: input.closest('.cart-quantity-input'), itemWrapper: input.closest('.cart-item-qty-wrapper') } : null;
    }

    reEnableButtonsForVariant(variantId) {
      const ctx = this._getVariantQtyContext(variantId);
      const buttons = ctx?.qtyWrapper?.querySelectorAll('.cart-qty-btn');
      if (buttons) buttons.forEach(btn => { btn.disabled = false; btn.style.pointerEvents = ''; btn.style.opacity = ''; });
    }

    showErrorForVariant(variantId, errorMessage) {
      this.querySelectorAll('[data-render-error]').forEach(el => { el.textContent = ''; el.classList.add('hidden'); });
      const ctx = this._getVariantQtyContext(variantId);
      const err = ctx?.itemWrapper?.querySelector('[data-render-error]');
      if (err) { err.textContent = errorMessage; err.classList.remove('hidden'); }
    }

    hideErrorForVariant(variantId) {
      const ctx = this._getVariantQtyContext(variantId);
      const err = ctx?.itemWrapper?.querySelector('[data-render-error]');
      if (err) { err.textContent = ''; err.classList.add('hidden'); }
    }

    updateCartIconCount(itemCount) {
      // Update cart icon count in header
      if (!this._cartBubbleCache || this._cartBubbleCache.length === 0) {
        this._cartBubbleCache = Array.from(document.querySelectorAll('.cart-count-bubble'));
      }

      // Create bubbles if needed
      if (this._cartBubbleCache.length === 0 && itemCount > 0) {
        const cartElements = document.querySelectorAll('button.cart, a.cart');
        cartElements.forEach(element => {
          const svg = element.querySelector('svg');
          if (svg && !element.querySelector('.cart-count-bubble')) {
            const bubbleContainer = document.createElement('div');
            bubbleContainer.className = 'cart-count-bubble';
            const span = document.createElement('span');
            span.setAttribute('aria-hidden', 'true');
            bubbleContainer.appendChild(span);
            element.appendChild(bubbleContainer);
            this._cartBubbleCache.push(bubbleContainer);
          }
        });
      }

      this._cartBubbleCache.forEach(bubble => {
        let span = bubble.querySelector('span[aria-hidden="true"]');
        if (!span) {
          span = document.createElement('span');
          span.setAttribute('aria-hidden', 'true');
          bubble.appendChild(span);
        }

        if (itemCount > 0 && itemCount < 100) {
          span.textContent = itemCount;
        } else if (itemCount >= 100) {
          span.textContent = '99+';
        } else {
          span.textContent = '';
        }

        if (itemCount > 0) {
          bubble.style.display = '';
        } else {
          bubble.style.setProperty('display', 'none', 'important');
        }
      });
    }

    async refreshCart() {
      try {
        await this.cartState.fetchCart();
      } catch (error) {
        console.error('Error refreshing cart:', error);

        // Show user-friendly error message
        this.showCartError('Unable to update cart. Please check your connection and try again.');
      }
    }

    /**
     * Show error message in cart
     */
    showCartError(message) {
      // Try to find existing error container
      let errorContainer = this.querySelector('.cart-error-message');

      if (!errorContainer) {
        // Create error container if it doesn't exist
        errorContainer = document.createElement('div');
        errorContainer.className = 'cart-error-message';
        errorContainer.style.cssText = 'background: #fee; color: #c00; padding: 12px; margin: 12px; border-radius: 4px; text-align: center;';

        const header = this.querySelector('.cart-slide__header');
        if (header && header.nextSibling) {
          header.parentNode.insertBefore(errorContainer, header.nextSibling);
        } else {
          this.insertBefore(errorContainer, this.firstChild);
        }
      }

      errorContainer.textContent = message;
      errorContainer.style.display = 'block';

      // Auto-hide after 5 seconds
      setTimeout(() => {
        if (errorContainer) {
          errorContainer.style.display = 'none';
        }
      }, 5000);
    }

    syncQuantityInputs(cart) {
      if (!cart?.items?.length) return;
      const allInputs = this.querySelectorAll('.cart-qty-input[data-variant-id]');
      const byVariant = new Map(cart.items.map(item => [String(item.variant_id), item.quantity]));
      allInputs.forEach(input => {
        const qty = byVariant.get(input.dataset.variantId);
        if (qty != null && input.value != qty) input.value = qty;
      });
    }

    /**
     * Enrich cart items with compare_at_price from product JSON (cart.js does not include it).
     * Based on backup logic: fetch /products/{handle}.js to get variant compare_at_price.
     */
    async enrichCart(cart) {
      if (!cart || !cart.items || cart.items.length === 0) return;

      const handlesToFetch = new Set();
      cart.items.forEach(item => {
        if (item.handle && !this._productDataCache[item.handle]) {
          handlesToFetch.add(item.handle);
        }
      });

      if (handlesToFetch.size > 0) {
        await Promise.all(Array.from(handlesToFetch).map(async (handle) => {
          try {
            const response = await fetch(`/products/${handle}.js`);
            const product = await response.json();
            this._productDataCache[handle] = product;
          } catch (e) {
            console.error(`Failed to fetch product data for ${handle}`, e);
            this._productDataCache[handle] = { error: true };
          }
        }));
      }

      let updated = false;
      let totalCompareAtSavings = 0;

      const updatedItems = cart.items.map(item => {
        const product = this._productDataCache[item.handle];
        let itemUpdated = false;
        let newItem = item;

        if (product && !product.error && product.variants?.length) {
          const variant = product.variants.find(v => String(v.id) === String(item.variant_id));
          if (variant && variant.compare_at_price > variant.price) {
            const newValues = {
              compare_at_price: variant.compare_at_price,
              compare_at_line_price: variant.compare_at_price * item.quantity
            };

            totalCompareAtSavings += (variant.compare_at_price - variant.price) * item.quantity;

            if (item.compare_at_price !== newValues.compare_at_price ||
              item.compare_at_line_price !== newValues.compare_at_line_price) {
              itemUpdated = true;
              newItem = { ...item, ...newValues };
            }
          }
        }

        if (itemUpdated) {
          updated = true;
          return newItem;
        }
        return item;
      });

      const previousSavings = Number(cart.total_compare_at_savings) || 0;
      if (updated || totalCompareAtSavings !== previousSavings) {
        const enrichedCart = {
          ...cart,
          items: updatedItems,
          total_compare_at_savings: totalCompareAtSavings
        };
        // Update cart silently - do NOT dispatch cart:updated (breaks progress bar)
        this.cartState.cart = this.cartState.enrichCartData(enrichedCart);
        if (updated) {
          this.renderCartItems(this.cartState.cart.items);
        }
        this.updateSummary(this.cartState.cart);
      }
    }

    updateGoals(cart) {
      const shippingBar = this.querySelector('shipping-bar');
      if (shippingBar && cart) {
        if (typeof shippingBar.updateFromCart === 'function') {
          shippingBar.updateFromCart(cart);
        } else {
          const progressTotal = typeof window.getProgressBarCartTotal === 'function'
            ? window.getProgressBarCartTotal(cart)
            : cart.total_price;
          if (progressTotal != null) {
            if (typeof shippingBar.updateCartTotals === 'function') {
              shippingBar.updateCartTotals(progressTotal);
            } else {
              shippingBar.setAttribute('data-cart-totals', progressTotal);
            }
          }
        }
      }

      // Update gift block (pass full cart for price/qty rule + auto-add)
      const giftBlock = this.querySelector('gift-block-cart');
      if (giftBlock && typeof giftBlock.checkUnlock === 'function') {
        giftBlock.checkUnlock(cart);
      }

      // Sync addon checkboxes (checked = variant in cart)
      this.syncAddonCheckboxes(cart);
    }

    /**
     * Sync addon product toggles to match cart (checked if variant is in cart)
     */
    syncAddonCheckboxes(cart) {
      const items = cart?.items || [];
      const variantIdsInCart = new Set(items.map(item => String(item.variant_id)));
      this.querySelectorAll('.product-addons__item[data-variant-id]').forEach(itemEl => {
        const variantId = itemEl.dataset.variantId;
        const toggle = itemEl.querySelector('.product-addons__toggle');
        const inCart = !!variantId && variantIdsInCart.has(variantId);
        if (toggle && variantId) {
          toggle.checked = inCart;
        }
        // Hippie Farms sandbox edit #8 (2026-09-21): a product already in the cart leaves the add-on list
        itemEl.classList.toggle('product-addons__item--in-cart', inCart);
      });
      this.querySelectorAll('.cart-slide__product-addons').forEach(block => {
        const visible = block.querySelectorAll('.product-addons__item[data-variant-id]:not(.product-addons__item--in-cart)').length;
        block.classList.toggle('cart-slide__product-addons--empty', visible === 0);
      });
    }

    /**
     * Addon toggle: checked = add variant to cart, unchecked = remove
     */
    async handleAddonToggle(toggle, variantId) {
      const wasChecked = toggle.checked;
      toggle.disabled = true;
      try {
        if (wasChecked) {
          await this.cartState.addItem(variantId, 1);
        } else {
          await this.cartState.updateItemById(variantId, 0);
        }
      } catch (error) {
        console.error('Addon toggle error:', error);
        toggle.checked = !wasChecked;
      } finally {
        toggle.disabled = false;
      }
    }

    setupPolicyAgreement() {
      // Set up policy checkbox listener
      setTimeout(() => {
        const checkbox = this.querySelector('.cart-slide__policy input[type="checkbox"]');
        if (checkbox) {
          checkbox.addEventListener('change', () => {
            this.toggleCheckout();
          });
        }
      }, 100);
    }

    startReserveTimer(seconds) {
      this.pauseTimer();
      this.reserveEndTime = Date.now() + seconds * 1000;
      this.updateTimerDisplay();

      this.timerInterval = setInterval(() => {
        this.updateTimerDisplay();
      }, 1000);
    }

    pauseTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    }

    updateTimerDisplay() {
      const timerElement = this.querySelector('.cart-slide__reserved strong');
      if (!timerElement) return;

      const remaining = Math.max(0, this.reserveEndTime - Date.now());
      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);

      timerElement.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

      if (remaining <= 0) {
        this.pauseTimer();
      }
    }

    /**
     * Restore discount UI state from sessionStorage so page refresh keeps applied view
     */
    restoreDiscountUIFromStorage() {
      const discountWrapper = this.querySelector('.cart-slide__discount');
      if (!discountWrapper) return;

      let storedCode = null;
      try {
        storedCode = window.sessionStorage ? sessionStorage.getItem('cartDrawerDiscountCode') : null;
      } catch (e) {
        storedCode = null;
      }

      const appliedDiv = discountWrapper.querySelector('.cart-slide__discount-applied');
      const inputGroup = discountWrapper.querySelector('.cart-slide__discount-input-group');
      const input = discountWrapper.querySelector('.cart-slide__input');
      const codeSpan = discountWrapper.querySelector('.cart-slide__discount-code');

      // If we have a stored code, show applied state (same UI as when user first applied)
      if (storedCode && appliedDiv && inputGroup && codeSpan) {
        codeSpan.textContent = storedCode;
        appliedDiv.classList.add('active');
        inputGroup.style.display = 'none';
        if (input) input.value = '';
      } else {
        // No stored code – make sure input is visible and applied block is hidden
        if (appliedDiv) appliedDiv.classList.remove('active');
        if (inputGroup) inputGroup.style.display = 'flex';
      }
    }

    async applyDiscount() {
      const input = this.querySelector('.cart-slide__input');
      const code = input?.value?.trim();

      if (!code) return;

      try {
        await this.applyDiscountViaIframe(code);

        // Show applied discount (original behaviour)
        const appliedDiv = this.querySelector('.cart-slide__discount-applied');
        const codeSpan = appliedDiv?.querySelector('.cart-slide__discount-code');

        if (appliedDiv && codeSpan) {
          codeSpan.textContent = code;
          appliedDiv.classList.add('active');

          // Hide input group
          const inputGroup = this.querySelector('.cart-slide__discount-input-group');
          if (inputGroup) inputGroup.style.display = 'none';
        }

        // Clear input
        if (input) input.value = '';

        // Remember applied code so refresh pe bhi yehi state rahe
        try {
          if (window.sessionStorage) {
            sessionStorage.setItem('cartDrawerDiscountCode', code);
          }
        } catch (e) {}

        // Refresh cart to get updated totals
        await this.refreshCart();
      } catch (error) {
        console.error('Error applying discount:', error);
        alert('Failed to apply discount code');
      }
    }

    async removeDiscount() {
      try {
        // Remove discount via Cart API so summary updates (Discount row + subtotal)
        await this.cartState.setDiscount('');

        // Also clear from checkout session so wahan se bhi remove ho jaye
        try {
          await this.removeDiscountViaIframe();
        } catch (iframeError) {
          console.warn('Discount iframe clear failed:', iframeError);
        }

        // Hide applied discount UI (original behaviour)
        const appliedDiv = this.querySelector('.cart-slide__discount-applied');
        if (appliedDiv) {
          appliedDiv.classList.remove('active');
        }

        // Clear and show input
        const input = this.querySelector('.cart-slide__input');
        if (input) input.value = '';
        const inputGroup = this.querySelector('.cart-slide__discount-input-group');
        if (inputGroup) inputGroup.style.display = 'flex';

        // Clear stored code so refresh ke baad dubara input visible ho
        try {
          if (window.sessionStorage) {
            sessionStorage.removeItem('cartDrawerDiscountCode');
          }
        } catch (e) {}

        // Refresh cart to get updated totals
        await this.refreshCart();
      } catch (error) {
        console.error('Error removing discount:', error);
        // Fallback: try iframe clear then refresh (same UX as pehle)
        try {
          await this.removeDiscountViaIframe();
          const appliedDiv = this.querySelector('.cart-slide__discount-applied');
          if (appliedDiv) appliedDiv.classList.remove('active');
          const inputGroup = this.querySelector('.cart-slide__discount-input-group');
          if (inputGroup) inputGroup.style.display = 'flex';

          try {
            if (window.sessionStorage) {
              sessionStorage.removeItem('cartDrawerDiscountCode');
            }
          } catch (e) {}

          await this.refreshCart();
        } catch (e2) {
          console.error('Fallback discount remove failed:', e2);
        }
      }
    }

    _discountIframePromise(path) {
      return new Promise((resolve, reject) => {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        if (path !== '/discount/clear') iframe.name = 'discount-iframe';
        document.body.appendChild(iframe);
        const done = () => { try { document.body.removeChild(iframe); } catch (_) { } };
        iframe.onload = () => { setTimeout(() => { done(); resolve(); }, 500); };
        iframe.onerror = () => { done(); reject(new Error('Failed to apply discount')); };
        iframe.src = path;
      });
    }

    applyDiscountViaIframe(code) {
      return this._discountIframePromise(`/discount/${encodeURIComponent(code)}`);
    }

    removeDiscountViaIframe() {
      return this._discountIframePromise('/discount/clear');
    }
  }

  customElements.define('cart-slide', CartSlide);

  const shouldRedirectToCartPage = () => window.themeCartSettings?.addBehavior === 'page';

  window.openCartDrawer = function (openerEl = null) {
    if (shouldRedirectToCartPage()) {
      window.location.assign(window.routes?.cart_url || '/cart');
      return;
    }
    const cartSlide = document.querySelector('cart-slide');
    if (cartSlide && !cartSlide.classList.contains('active')) {
      cartSlide.openCart({ openerEl });
    }
  };

  // Set up direct listeners for cart icon buttons
  function setupDirectListeners() {
    const selectors = ['.js-cart-toggle', '.open-cart-btn', 'button.js-cart-toggle', 'button.open-cart-btn', '.cart.js-cart-toggle'];
    const allButtons = new Set();
    selectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(btn => {
        if (!isAddToCartButton(btn)) allButtons.add(btn);
      });
    });

    allButtons.forEach(btn => {
      if (btn.dataset.cartListenerAttached === 'true') return;

      const clickHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        window.openCartDrawer(btn);
      };

      btn.addEventListener('click', clickHandler, true);
      btn.dataset.cartListenerAttached = 'true';
    });
  }

  document.addEventListener('click', (e) => {
    const target = e.target;
    const isSubmitAddToCart = target.tagName === 'BUTTON' && target.type === 'submit' &&
      target.closest('form[action*="/cart/add"]') && !target.closest('.js-cart-toggle, .open-cart-btn');
    if (isAddToCartButton(target) || isSubmitAddToCart || (target.closest('.product_atc_button, [data-add-to-cart], .add-to-cart') && !target.closest('.js-cart-toggle, .open-cart-btn')))
      return;
    const cartButton = target.closest('.js-cart-toggle, .open-cart-btn, button.js-cart-toggle, button.open-cart-btn, .cart.js-cart-toggle');
    if (cartButton && !isAddToCartButton(cartButton)) {
      e.preventDefault();
      e.stopPropagation();
      window.openCartDrawer(cartButton);
    }
  }, true);

  function runCartInit() {
    setupDirectListeners();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runCartInit);
  } else {
    runCartInit();
  }

  // Retry setup for dynamically added buttons
  const retryDelays = [500, 1000, 2000, 3000];
  retryDelays.forEach(delay => {
    setTimeout(() => {
      setupDirectListeners();
    }, delay);
  });

  // MutationObserver for dynamically added buttons
  if (typeof MutationObserver !== 'undefined') {
    const cartButtonSelector = '.cart, .js-cart-toggle, .open-cart-btn, button.cart';
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.addedNodes.length === 0) continue;
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.classList?.contains('cart') || node.classList?.contains('js-cart-toggle') || node.classList?.contains('open-cart-btn') ||
            node.querySelectorAll?.(cartButtonSelector).length > 0) {
            setupDirectListeners();
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Gift block cart – price/qty rule, remaining display, auto-add gift when unlocked
  if (!customElements.get('gift-block-cart')) {
    customElements.define(
      'gift-block-cart',
      class GiftBlockCart extends HTMLElement {
        constructor() {
          super();
          this.priceUnlock = parseInt(this.dataset.priceunlock || '0', 10);
          this.qtyUnlock = parseInt(this.dataset.qtyunlock || '1', 10);
          this.ruleType = (this.dataset.ruleType || 'price').toLowerCase();
          this.lockedClass = 'cart-slide__gift-item--locked';
          this._addingGift = false;
          this._removingGift = false;
          /** When locked, we haven't added gift for this session; when unlocked we add once then set true */
          this._giftAddedForUnlock = false;
        }

        connectedCallback() {
          this.giftContainer = this.querySelector('.cart-slide__gift-item');
          this.giftNeedEl = this.closest('cart-slide')?.querySelector('[data-giftneed]') || document.querySelector('[data-giftneed]');
          this.giftTitleLocked = this.closest('cart-slide')?.querySelector('[data-gift-title-locked]') || document.querySelector('[data-gift-title-locked]');
          this.giftTitleUnlocked = this.closest('cart-slide')?.querySelector('[data-gift-title-unlocked]') || document.querySelector('[data-gift-title-unlocked]');
          this.giftVariantId = (this.dataset.giftid || '').toString().trim();
          document.addEventListener('cart:updated', (e) => {
            const cart = e.detail;
            if (cart) this.checkUnlock(cart);
          });
        }

        /**
         * Update gift block from cart (price or qty rule), show remaining, auto-add when unlocked
         */
        checkUnlock(cart) {
          if (!cart) return;
          this.giftVariantId = (this.dataset.giftid || '').toString().trim();
          const isPriceRule = this.ruleType !== 'qty';
          const paidTotal = this.getPaidCartTotal(cart);
          const paidItemCount = this.getPaidItemCount(cart);
          let isUnlocked;
          let remaining;
          if (isPriceRule) {
            isUnlocked = this.priceUnlock > 0 && paidTotal >= this.priceUnlock;
            remaining = Math.max(0, this.priceUnlock - paidTotal);
          } else {
            isUnlocked = this.qtyUnlock > 0 && paidItemCount >= this.qtyUnlock;
            remaining = Math.max(0, this.qtyUnlock - paidItemCount);
          }
          if (this.giftNeedEl) {
            if (isPriceRule) {
              if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
                this.giftNeedEl.textContent = Shopify.formatMoney(remaining, window.money_format);
              } else {
                this.giftNeedEl.textContent = (remaining / 100).toFixed(2);
              }
            } else {
              this.giftNeedEl.textContent = String(remaining);
            }
          }
          if (isUnlocked) {
            this.unlockGift();
            this.autoAddGiftOnce(cart);
          } else {
            this.removeGiftWhenLocked(cart);
            this.lockGift();
          }
        }

        getPaidCartTotal(cart) {
          if (typeof window.getProgressBarCartTotal === 'function') {
            return window.getProgressBarCartTotal(cart);
          }
          let total = 0;
          (cart.items || []).forEach((item) => {
            if (this.isAutoGiftLine(item)) return;
            total += ((item.final_line_price != null && Number.isFinite(Number(item.final_line_price))) ? Number(item.final_line_price) : (Number(item.line_price) || Number(item.original_line_price) || 0));
          });
          return total;
        }

        getPaidItemCount(cart) {
          return (cart.items || []).reduce((sum, item) => {
            if (this.isAutoGiftLine(item)) return sum;
            return sum + (Number(item.quantity) || 0);
          }, 0);
        }

        isAutoGiftLine(item) {
          const props = item?.properties || {};
          return props._shipping_bar_gift === '1' || props._shipping_bar_gift === 1
            || props._gift_block_cart === '1' || props._gift_block_cart === 1;
        }

        getCartState() {
          return (this.closest('cart-slide') && this.closest('cart-slide').cartState) || window.VanillaCartState;
        }

        isGiftBlockAutoLine(item) {
          if (!item || !this.giftVariantId) return false;
          const sameVariant = String(item.variant_id) === String(this.giftVariantId) || String(item.id) === String(this.giftVariantId);
          if (!sameVariant) return false;

          const props = item.properties || {};
          return props._gift_block_cart === '1' || props._gift_block_cart === 1;
        }

        removeGiftWhenLocked(cart) {
          const cartState = this.getCartState();
          if (!cartState || !this.giftVariantId || this._removingGift || this._addingGift) return;
          if (!cart || !Array.isArray(cart.items) || typeof cartState.updateItemByLine !== 'function') {
            this._giftAddedForUnlock = false;
            this.clearGiftAddedFlag();
            return;
          }

          const linesToRemove = cart.items
            .map((item, index) => ({ item, line: index + 1 }))
            .filter(({ item }) => this.isGiftBlockAutoLine(item))
            .map(({ line }) => line)
            .sort((a, b) => b - a);

          this._giftAddedForUnlock = false;
          this.clearGiftAddedFlag();
          if (!linesToRemove.length) return;

          this._removingGift = true;
          (async () => {
            try {
              for (const line of linesToRemove) {
                await cartState.updateItemByLine(line, 0);
              }
            } catch (err) {
              console.error('Gift auto-remove failed:', err);
            } finally {
              this._removingGift = false;
            }
          })();
        }

        _giftStorageKey() {
          return 'cartGiftAdded_' + (this.giftVariantId || '');
        }

        getGiftAddedFlag() {
          try {
            return sessionStorage.getItem(this._giftStorageKey()) === '1';
          } catch (e) {
            return false;
          }
        }

        setGiftAddedFlag() {
          try {
            sessionStorage.setItem(this._giftStorageKey(), '1');
          } catch (e) { }
        }

        clearGiftAddedFlag() {
          try {
            sessionStorage.removeItem(this._giftStorageKey());
          } catch (e) { }
        }

        /**
         * When unlocked: add 1 gift once per unlock. Use sessionStorage so we don't add again on page reload.
         */
        autoAddGiftOnce(cart) {
          if (!this.giftVariantId || this._addingGift || this._removingGift) return;
          const giftIdNum = parseInt(this.giftVariantId, 10);
          if (isNaN(giftIdNum)) return;
          const alreadyInCart = (cart.items || []).some((item) => this.isGiftBlockAutoLine(item));
          if (alreadyInCart) {
            this._giftAddedForUnlock = true;
            this.setGiftAddedFlag();
            return;
          }
          const cartState = this.getCartState();
          if (!cartState || typeof cartState.addItem !== 'function') return;
          this._addingGift = true;
          this._giftAddedForUnlock = true;
          this.setGiftAddedFlag();
          cartState.addItem(this.giftVariantId, 1, { _gift_block_cart: '1' })
            .then(() => { this._addingGift = false; })
            .catch((err) => {
              this._addingGift = false;
              this._giftAddedForUnlock = false;
              this.clearGiftAddedFlag();
              console.error('Gift auto-add failed:', err);
            });
        }

        unlockGift() {
          if (this.giftContainer) this.giftContainer.classList.remove(this.lockedClass);
          if (this.giftTitleLocked) this.giftTitleLocked.style.display = 'none';
          if (this.giftTitleUnlocked) this.giftTitleUnlocked.style.display = '';
        }

        lockGift() {
          if (this.giftContainer) this.giftContainer.classList.add(this.lockedClass);
          if (this.giftTitleLocked) this.giftTitleLocked.style.display = '';
          if (this.giftTitleUnlocked) this.giftTitleUnlocked.style.display = 'none';
        }
      }
    );
  }

  (function () {
    function updateCartIconFromCart(cart) {
      const cartSlide = document.querySelector('cart-slide');
      if (cartSlide && typeof cartSlide.updateCartIconCount === 'function')
        cartSlide.updateCartIconCount(cart.item_count || 0);
    }
    document.addEventListener('cart:updated', (e) => {
      if (e.detail && e.detail.item_count != null) updateCartIconFromCart(e.detail);
    });
    function fetchAndUpdateCartIcon() {
      fetch('/cart.js').then(r => r.json()).then(updateCartIconFromCart).catch(() => { });
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fetchAndUpdateCartIcon);
    else fetchAndUpdateCartIcon();
  })();

})();
